import { CommunicationPage, MessageResponse } from "client/openapi";

/** Where the next (older) page starts: the last message of the page before. */
export type Cursor = { before: string; beforeId: number };

/**
 * What the Communication view has loaded: the date range's messages newest
 * first, a page at a time; this admin's unread conversations' other
 * messages, whatever their date (they come with the first page); and where
 * the next page starts (null once every page is loaded).
 */
export type Feed = {
  messages: MessageResponse[];
  unread: MessageResponse[];
  next: Cursor | null;
};

function nextPage(page: CommunicationPage): Cursor | null {
  return page.next_before && page.next_before_id != null
    ? { before: page.next_before, beforeId: page.next_before_id }
    : null;
}

/** The first page, as the whole feed. */
export function firstPage(page: CommunicationPage): Feed {
  return { messages: page.messages, unread: page.unread, next: nextPage(page) };
}

/** The next page, at the end. */
export function withOlderPage(feed: Feed, page: CommunicationPage): Feed {
  return {
    ...feed,
    messages: [...feed.messages, ...page.messages],
    next: nextPage(page),
  };
}

/** Whether a message is past the cursor in the server's order (older). */
function isPast(message: MessageResponse, cursor: Cursor): boolean {
  const at = new Date(message.created_at).getTime();
  const before = new Date(cursor.before).getTime();
  return at < before || (at === before && message.id < cursor.beforeId);
}

/**
 * A reloaded first page in place of the part of the feed it covers: the
 * messages as new as its last one or newer go, the older pages stay (the
 * feed is in the server's order, so they are the ones after its last
 * message), and the unread ones are replaced, except those in the dates
 * (from on) and older than its last message: they belong to pages further
 * down, so they stay, read or not, as they would with every page loaded.
 * When it is the only page, or it doesn't reach what was loaded (more came
 * in than a page holds), it is the whole feed.
 */
export function withFirstPage(
  feed: Feed,
  page: CommunicationPage,
  from: Date
): Feed {
  const reloaded = firstPage(page);
  const next = reloaded.next;
  if (!next) return reloaded;
  const unread = [
    ...reloaded.unread,
    ...feed.unread.filter(
      (m) =>
        new Date(m.created_at).getTime() >= from.getTime() && isPast(m, next)
    ),
  ];
  const end = feed.messages.map((m) => m.id).lastIndexOf(next.beforeId);
  if (end < 0) return { ...reloaded, unread };
  return {
    messages: [...reloaded.messages, ...feed.messages.slice(end + 1)],
    unread,
    next: feed.next,
  };
}

/**
 * Every loaded message once. A send answered by email is in two rows (its
 * text row and the email thread's), so a message is its row and id.
 */
export function feedMessages(feed: Feed | null): MessageResponse[] {
  const seen = new Set<string>();
  return [...(feed?.messages ?? []), ...(feed?.unread ?? [])].filter(
    (message) => {
      const key = `${message.row_key}:${message.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }
  );
}
